/**
 * Channel Configs Controller
 *
 * State management and RPC operations for per-agent channel configurations.
 * Follows the same pattern as the email controller in igloobot.ts.
 */

import type {
  ChannelConfigEntry,
  ChannelTestResult,
  AgentOption,
  ChannelConfigsProps,
} from "../views/channel-configs-admin.ts";

// --------------- State ---------------

export type ChannelConfigsState = {
  client: { request: <T>(method: string, params?: unknown) => Promise<T> } | null;
  connected: boolean;
  agentsList: { agents: Array<{ id: string; name?: string; identity?: { name?: string } }> } | null;
  ccSelectedAgentId: string | null;
  ccSelectedChannelType: string | null;
  ccSelectedAccountId: string;
  ccAgents: AgentOption[];
  ccConfig: ChannelConfigEntry | null;
  ccLoading: boolean;
  ccError: string | null;
  ccSaving: boolean;
  ccForm: {
    credentials: Record<string, unknown>;
    settings: Record<string, unknown>;
    label: string;
    isActive: boolean;
  } | null;
  ccTesting: boolean;
  ccTestResult: ChannelTestResult | null;
  ccAllConfigs: ChannelConfigEntry[];
  ccAllConfigsLoading: boolean;
};

export const CHANNEL_CONFIGS_STATE_DEFAULTS: Omit<
  ChannelConfigsState,
  "client" | "connected" | "agentsList"
> = {
  ccSelectedAgentId: null,
  ccSelectedChannelType: "telegram",
  ccSelectedAccountId: "default",
  ccAgents: [],
  ccConfig: null,
  ccLoading: false,
  ccError: null,
  ccSaving: false,
  ccForm: null,
  ccTesting: false,
  ccTestResult: null,
  ccAllConfigs: [],
  ccAllConfigsLoading: false,
};

// --------------- Helpers ---------------

function friendlyError(err: unknown): string {
  const msg = String(err).toLowerCase();
  if (msg.includes("method not found") || msg.includes("not registered")) {
    return "i-GlooBot plugin is not loaded. Ensure the igloobot-core extension is enabled and the database is configured.";
  }
  if (msg.includes("connection refused") || msg.includes("econnrefused")) {
    return "Database connection failed. Check that PostgreSQL is running and IGLOOBOT_DATABASE_URL is set.";
  }
  return String(err);
}

// --------------- RPC Types ---------------

type ConfigResult = { ok: boolean; config: ChannelConfigEntry | null; error?: string };
type ListResult = { ok: boolean; configs: ChannelConfigEntry[]; error?: string };
type DeleteResult = { ok: boolean; deleted: boolean; error?: string };
type TestResult = { ok: boolean; result: ChannelTestResult; error?: string };

// --------------- Operations ---------------

export async function loadChannelConfig(state: ChannelConfigsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.ccSelectedAgentId;
  const channelType = state.ccSelectedChannelType;
  if (!agentId || !channelType) {
    return;
  }

  state.ccLoading = true;
  state.ccError = null;
  try {
    const res = await state.client.request<ConfigResult>("igloobot.channels.config.get", {
      agentId,
      channelType,
      accountId: state.ccSelectedAccountId,
    });
    if (res.ok) {
      state.ccConfig = res.config ?? null;
      state.ccForm = res.config
        ? {
            credentials: { ...res.config.credentials },
            settings: { ...res.config.settings },
            label: res.config.label,
            isActive: res.config.isActive,
          }
        : {
            credentials: {},
            settings: {},
            label: "",
            isActive: true,
          };
    } else {
      state.ccError = res.error ?? "Failed to load channel config";
    }
  } catch (err) {
    state.ccError = friendlyError(err);
  } finally {
    state.ccLoading = false;
  }
}

export async function loadAllChannelConfigs(state: ChannelConfigsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.ccAllConfigsLoading = true;
  try {
    const res = await state.client.request<ListResult>("igloobot.channels.config.list", {});
    if (res.ok) {
      state.ccAllConfigs = res.configs;
    }
  } catch {
    // Non-critical — all configs table just won't update
  } finally {
    state.ccAllConfigsLoading = false;
  }
}

export async function saveChannelConfig(state: ChannelConfigsState): Promise<void> {
  if (!state.client || !state.connected || !state.ccForm) {
    return;
  }
  const agentId = state.ccSelectedAgentId;
  const channelType = state.ccSelectedChannelType;
  if (!agentId || !channelType) {
    return;
  }

  state.ccSaving = true;
  state.ccError = null;
  try {
    const res = await state.client.request<ConfigResult>("igloobot.channels.config.upsert", {
      agentId,
      channelType,
      accountId: state.ccSelectedAccountId,
      label: state.ccForm.label,
      credentials: state.ccForm.credentials,
      settings: state.ccForm.settings,
      isActive: state.ccForm.isActive,
    });
    if (res.ok && res.config) {
      state.ccConfig = res.config;
      state.ccForm = {
        credentials: { ...res.config.credentials },
        settings: { ...res.config.settings },
        label: res.config.label,
        isActive: res.config.isActive,
      };
    } else {
      state.ccError = res.error ?? "Failed to save channel config";
    }
  } catch (err) {
    state.ccError = friendlyError(err);
  } finally {
    state.ccSaving = false;
    // Refresh the all-configs list
    void loadAllChannelConfigs(state);
  }
}

export async function deleteChannelConfig(state: ChannelConfigsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.ccSelectedAgentId;
  const channelType = state.ccSelectedChannelType;
  if (!agentId || !channelType) {
    return;
  }

  state.ccSaving = true;
  state.ccError = null;
  try {
    const res = await state.client.request<DeleteResult>("igloobot.channels.config.delete", {
      agentId,
      channelType,
      accountId: state.ccSelectedAccountId,
    });
    if (res.ok) {
      state.ccConfig = null;
      state.ccForm = { credentials: {}, settings: {}, label: "", isActive: true };
      state.ccTestResult = null;
    } else {
      state.ccError = res.error ?? "Failed to delete channel config";
    }
  } catch (err) {
    state.ccError = friendlyError(err);
  } finally {
    state.ccSaving = false;
    void loadAllChannelConfigs(state);
  }
}

export async function testChannelConfig(state: ChannelConfigsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.ccSelectedAgentId;
  const channelType = state.ccSelectedChannelType;
  if (!agentId || !channelType) {
    return;
  }

  state.ccTesting = true;
  state.ccTestResult = null;
  state.ccError = null;
  try {
    const res = await state.client.request<TestResult>("igloobot.channels.config.test", {
      agentId,
      channelType,
      accountId: state.ccSelectedAccountId,
    });
    if (res.ok && res.result) {
      state.ccTestResult = res.result;
    } else {
      state.ccError = res.error ?? "Test failed";
    }
  } catch (err) {
    state.ccError = friendlyError(err);
  } finally {
    state.ccTesting = false;
  }
}

export function updateChannelConfigForm(
  state: ChannelConfigsState,
  section: "credentials" | "settings",
  field: string,
  value: unknown,
): void {
  if (!state.ccForm) {
    return;
  }
  state.ccForm[section] = { ...state.ccForm[section], [field]: value };
  state.ccForm = { ...state.ccForm };
}

export function selectChannelConfigAgent(state: ChannelConfigsState, agentId: string): void {
  state.ccSelectedAgentId = agentId;
  state.ccConfig = null;
  state.ccForm = null;
  state.ccError = null;
  state.ccTestResult = null;
  void loadChannelConfig(state);
}

export function selectChannelConfigType(state: ChannelConfigsState, channelType: string): void {
  state.ccSelectedChannelType = channelType;
  state.ccConfig = null;
  state.ccForm = null;
  state.ccError = null;
  state.ccTestResult = null;
  void loadChannelConfig(state);
}

export function selectChannelConfigAccountId(state: ChannelConfigsState, accountId: string): void {
  state.ccSelectedAccountId = accountId || "default";
  state.ccConfig = null;
  state.ccForm = null;
  state.ccError = null;
  state.ccTestResult = null;
  void loadChannelConfig(state);
}

// --------------- Props Builder ---------------

function buildAgentOptions(state: ChannelConfigsState): AgentOption[] {
  if (!state.agentsList?.agents) {
    return [];
  }
  return state.agentsList.agents.map((a) => ({
    id: a.id,
    name: a.identity?.name ?? a.name ?? a.id,
  }));
}

export function buildChannelConfigsProps(state: ChannelConfigsState): ChannelConfigsProps {
  return {
    loading: state.ccLoading,
    error: state.ccError,
    saving: state.ccSaving,
    config: state.ccConfig,
    form: state.ccForm,
    testing: state.ccTesting,
    testResult: state.ccTestResult,
    agents: buildAgentOptions(state),
    selectedAgentId: state.ccSelectedAgentId,
    selectedChannelType: state.ccSelectedChannelType,
    selectedAccountId: state.ccSelectedAccountId,
    allConfigs: state.ccAllConfigs,
    onSelectAgent: (agentId) => selectChannelConfigAgent(state, agentId),
    onSelectChannelType: (channelType) => selectChannelConfigType(state, channelType),
    onSelectAccountId: (accountId) => selectChannelConfigAccountId(state, accountId),
    onRefresh: () => {
      void loadChannelConfig(state);
      void loadAllChannelConfigs(state);
    },
    onSave: () => void saveChannelConfig(state),
    onDelete: () => void deleteChannelConfig(state),
    onTest: () => void testChannelConfig(state),
    onFormChange: (section, field, value) => updateChannelConfigForm(state, section, field, value),
    onLabelChange: (value) => {
      if (state.ccForm) {
        state.ccForm = { ...state.ccForm, label: value };
      }
    },
    onActiveChange: (value) => {
      if (state.ccForm) {
        state.ccForm = { ...state.ccForm, isActive: value };
      }
    },
  };
}
